import type { QueryClient } from '@tanstack/react-query';
import { getPrefixes } from '@/data/mutations/registry';
import type { TextAtoms } from '@/types';
import { isTextAtomBearingQuery } from './queryKeyPredicates';

// ============================================================================
// Types
// ============================================================================

/** Minimal shape shared by all scene-like entities in the cache (IFountainScene, IScenePresentationDuration, etc.) */
export interface CacheableScene {
    id: number;
    text_atoms: TextAtoms[];
}

export interface CacheSnapshot {
    queryKey: readonly unknown[];
    data: unknown;
}

// ============================================================================
// Cache Scanning & Optimistic Transform
// ============================================================================

/**
 * Cancel all queries that contain text atoms, snapshot their current state,
 * apply a transform function to each scene in those caches, and return
 * a rollback function.
 *
 * The `transformScene` receives each scene object (which has a `text_atoms` array)
 * and should return the modified scene, or the original scene if no change is needed.
 *
 * This replaces the ~40-line cache-walking pattern that was duplicated 3 times
 * in useEditorTextAtom.ts.
 */
export async function optimisticTransform<T extends CacheableScene = CacheableScene>(
    queryClient: QueryClient,
    transformScene: (scene: T) => T,
): Promise<{ snapshots: CacheSnapshot[]; rollback: () => void }> {
    const snapshots: CacheSnapshot[] = [];

    // Cancel outgoing refetches for all text-atom-bearing queries
    const allCachedQueries = queryClient.getQueryCache().getAll();
    const matchingQueries = allCachedQueries.filter(q => isTextAtomBearingQuery(q.queryKey) && q.state.data);

    for (const query of matchingQueries) {
        await queryClient.cancelQueries({ queryKey: query.queryKey });
    }

    // Snapshot and transform
    for (const query of matchingQueries) {
        const { queryKey } = query;
        const data = query.state.data;

        snapshots.push({ queryKey, data });

        if (Array.isArray(data)) {
            queryClient.setQueryData(queryKey, (data as T[]).map(transformScene));
        }
    }

    const rollback = () => {
        for (const { queryKey, data } of snapshots) {
            queryClient.setQueryData(queryKey, data);
        }
    };

    return { snapshots, rollback };
}

// ============================================================================
// Invalidation
// ============================================================================

/**
 * Invalidate the caches affected by a text-atom mutation, after it settles.
 *
 * The fan-out IS the registry's textAtom row — one source for "what refreshes
 * after a text-atom write". Replaced the 12-target shotgun (8 entity roots with
 * refetchType 'all', three ctx-keyed entity keys, and a facade alias) that
 * predated the registry; the pinned-then-narrowed diff lives in
 * textAtomCacheManager.test.ts (ADR 0010 stage 3).
 */
export function invalidateEditorCaches(queryClient: QueryClient): void {
    for (const prefix of getPrefixes('textAtom', 'update')) {
        queryClient.invalidateQueries({ queryKey: prefix });
    }
}

// ============================================================================
// Optimistic ID Resolution
// ============================================================================

/**
 * After a create mutation succeeds, replace the optimistic atom in all caches
 * with the real server-returned atom.
 */
export function replaceOptimisticAtom(queryClient: QueryClient, optimisticId: number, realAtom: TextAtoms, textableId: number): void {
    const allCachedQueries = queryClient.getQueryCache().getAll();

    for (const query of allCachedQueries) {
        const { queryKey } = query;
        const data = query.state.data;

        if (data && isTextAtomBearingQuery(queryKey)) {
            queryClient.setQueryData<CacheableScene[]>(queryKey, old => {
                if (!Array.isArray(old)) return old;
                return old.map(scene => {
                    if (scene.id === textableId) {
                        return {
                            ...scene,
                            text_atoms: scene.text_atoms.map(atom => (atom.id === optimisticId ? realAtom : atom)),
                        };
                    }
                    return scene;
                });
            });
        }
    }
}
